import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

// CS (caution statement)
const CAUTION_STATEMENT_OPTIONS = [
  { id: 0, label: 'None' },
  { id: 1, label: 'High NROI People' },
  { id: 2, label: 'High NROI People and Ctrl' },
  { id: 3, label: 'Minor Avoidant' },
  { id: 4, label: 'High Avoidant' }
];

// SA (sexual attraction)
const SEXUAL_ATTRACTION_OPTIONS = [
  { id: 1, label: 'Low' },
  { id: 2, label: 'Moderate' },
  { id: 3, label: 'High' }
];

// DA (data)
const DATA_OPTIONS = [
  { id: 1, label: 'Below Normal' },
  { id: 2, label: 'Normal' },
  { id: 3, label: 'Above Normal' }
];

// HS (high sexual)
const HIGH_SEXUAL_OPTIONS = [
  { id: 0, label: 'None' },
  { id: 1, label: 'Above Normal' },
  { id: 2, label: 'Significantly Above Normal' }
];

// D (discriminant)
const DISCRIMINATORY_OPTIONS = [
  { id: 0, label: 'None' },
  { id: 1, label: 'Yes' },
  { id: 2, label: 'Yes, Higher than all other groups' }
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function getOptionLabel(options, id) {
  if (id === null || id === undefined) return '—';
  const option = options.find(opt => opt.id === id);
  return option ? `${option.label} (${option.id})` : '—';
}

export default function ResultsSubmissionForm({ submissionMeta, onSubmitSuccess }) {
  const initialFormState = {
    cautionStatement: null,
    femaleAdult: {
      sexualAttraction: null,
      data: null,
      highSexual: null
    },
    maleAdult: {
      sexualAttraction: null,
      data: null,
      highSexual: null
    },
    femaleAdolescent: {
      sexualAttraction: null,
      data: null,
      highSexual: null
    },
    maleAdolescent: {
      sexualAttraction: null,
      data: null,
      highSexual: null
    },
    femaleChild: {
      sexualAttraction: null,
      data: null,
      highSexual: null,
      discriminatory: null
    },
    maleChild: {
      sexualAttraction: null,
      data: null,
      highSexual: null,
      discriminatory: null
    }
  };

  const [formData, setFormData] = useState(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [summaryStatement, setSummaryStatement] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [isNewTemplate, setIsNewTemplate] = useState(true);
  const [originalTemplate, setOriginalTemplate] = useState('');

  // Define tabs for the form
  const tabs = [
    { name: 'Caution Statement', id: 'cautionStatement' },
    { name: 'Female Adult', id: 'femaleAdult' },
    { name: 'Male Adult', id: 'maleAdult' },
    { name: 'Female Adolescent', id: 'femaleAdolescent' },
    { name: 'Male Adolescent', id: 'maleAdolescent' },
    { name: 'Female Child', id: 'femaleChild' },
    { name: 'Male Child', id: 'maleChild' },
    { name: 'Review & Submit', id: 'review' }
  ];

  const handleSelectChange = (category, subcategory, value) => {
    if (subcategory) {
      setFormData(prevData => ({
        ...prevData,
        [category]: {
          ...prevData[category],
          [subcategory]: value
        }
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [category]: value
      }));
    }
  };

  // Generate a unique key from all form selections for template lookup
  const generatePermutationKey = (data) => {
    const cs = data.cautionStatement ?? 'x';
    const fa = `${data.femaleAdult.sexualAttraction ?? 'x'}${data.femaleAdult.data ?? 'x'}${data.femaleAdult.highSexual ?? 'x'}`;
    const ma = `${data.maleAdult.sexualAttraction ?? 'x'}${data.maleAdult.data ?? 'x'}${data.maleAdult.highSexual ?? 'x'}`;
    const fad = `${data.femaleAdolescent.sexualAttraction ?? 'x'}${data.femaleAdolescent.data ?? 'x'}${data.femaleAdolescent.highSexual ?? 'x'}`;
    const mad = `${data.maleAdolescent.sexualAttraction ?? 'x'}${data.maleAdolescent.data ?? 'x'}${data.maleAdolescent.highSexual ?? 'x'}`;
    const fc = `${data.femaleChild.sexualAttraction ?? 'x'}${data.femaleChild.data ?? 'x'}${data.femaleChild.highSexual ?? 'x'}${data.femaleChild.discriminatory ?? 'x'}`;
    const mc = `${data.maleChild.sexualAttraction ?? 'x'}${data.maleChild.data ?? 'x'}${data.maleChild.highSexual ?? 'x'}${data.maleChild.discriminatory ?? 'x'}`;
    return `CS${cs}_FA${fa}_MA${ma}_FAd${fad}_MAd${mad}_FC${fc}_MC${mc}`;
  };

  // Check if all categories are complete (used for template lookup)
  const areCategoriesComplete = () => {
    if (formData.cautionStatement === null) return false;

    const categories = [
      'femaleAdult', 'maleAdult', 'femaleAdolescent',
      'maleAdolescent', 'femaleChild', 'maleChild'
    ];

    return categories.every(category => {
      const data = formData[category];
      if (category === 'femaleChild' || category === 'maleChild') {
        return data.sexualAttraction !== null &&
               data.data !== null &&
               data.highSexual !== null &&
               data.discriminatory !== null;
      }
      return data.sexualAttraction !== null &&
             data.data !== null &&
             data.highSexual !== null;
    });
  };

  // Full form validation including summary statement (for submission)
  const isFormValid = () => {
    if (!areCategoriesComplete()) return false;
    if (!summaryStatement.trim()) return false;
    return true;
  };

  // Fetch template when all categories are complete
  useEffect(() => {
    const fetchTemplate = async () => {
      if (!areCategoriesComplete()) {
        return;
      }

      setTemplateLoading(true);
      try {
        const db = getFirestore();
        const key = generatePermutationKey(formData);
        const docRef = doc(db, 'summary_templates', key);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const template = docSnap.data().template;
          setSummaryStatement(template);
          setOriginalTemplate(template);
          setIsNewTemplate(false);
        } else {
          setSummaryStatement('');
          setOriginalTemplate('');
          setIsNewTemplate(true);
        }
      } catch (err) {
        console.error('Error fetching template:', err);
      } finally {
        setTemplateLoading(false);
      }
    };

    fetchTemplate();
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      setError('Please complete all category sections and summary statement.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const db = getFirestore();
      const key = generatePermutationKey(formData);

      // Save/update template only if it's new or was edited
      const templateWasEdited = summaryStatement !== originalTemplate;
      if (isNewTemplate || templateWasEdited) {
        await setDoc(doc(db, 'summary_templates', key), {
          template: summaryStatement,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }

      // Create submission record with metadata from parent
      const submission = {
        ...submissionMeta,
        submittedAt: serverTimestamp(),
        results: formData,
        summaryStatement: summaryStatement
      };

      await addDoc(collection(db, 'evaluation_results'), submission);

      setFormData(initialFormState);
      setSummaryStatement('');
      setOriginalTemplate('');
      setIsNewTemplate(true);

      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (err) {
      console.error('Error submitting evaluation results:', err);
      setError('Failed to submit results. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isTabComplete = (tabId) => {
    if (tabId === 'cautionStatement') {
      return formData.cautionStatement !== null;
    } else if (tabId === 'review') {
      return true;
    } else {
      const data = formData[tabId];
      if (!data) return false;

      const hasDiscriminatory =
        tabId === 'femaleChild' ||
        tabId === 'maleChild';

      if (hasDiscriminatory) {
        return data.sexualAttraction !== null &&
               data.data !== null &&
               data.highSexual !== null &&
               data.discriminatory !== null;
      } else {
        return data.sexualAttraction !== null &&
               data.data !== null &&
               data.highSexual !== null;
      }
    }
  };

  const goToNextTab = () => {
    if (selectedTabIndex < tabs.length - 1) {
      setSelectedTabIndex(selectedTabIndex + 1);
    }
  };

  const goToPreviousTab = () => {
    if (selectedTabIndex > 0) {
      setSelectedTabIndex(selectedTabIndex - 1);
    }
  };

  const renderCategorySection = (category, includeDiscriminatory = false) => (
    <div className="bg-white rounded-md p-4">
      <h4 className="text-base font-medium text-gray-900 mb-4">{tabs.find(tab => tab.id === category).name}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Sexual Attraction */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sexual Attraction
          </label>
          <select
            className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            value={formData[category].sexualAttraction ?? ''}
            onChange={(e) => handleSelectChange(category, 'sexualAttraction', e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {SEXUAL_ATTRACTION_OPTIONS.map(option => (
              <option key={option.id} value={option.id}>
                {option.label} ({option.id})
              </option>
            ))}
          </select>
        </div>

        {/* Data */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data
          </label>
          <select
            className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            value={formData[category].data ?? ''}
            onChange={(e) => handleSelectChange(category, 'data', e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {DATA_OPTIONS.map(option => (
              <option key={option.id} value={option.id}>
                {option.label} ({option.id})
              </option>
            ))}
          </select>
        </div>

        {/* High Sexual */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            High Sexual
          </label>
          <select
            className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            value={formData[category].highSexual ?? ''}
            onChange={(e) => handleSelectChange(category, 'highSexual', e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">Select...</option>
            {HIGH_SEXUAL_OPTIONS.map(option => (
              <option key={option.id} value={option.id}>
                {option.label} ({option.id})
              </option>
            ))}
          </select>
        </div>

        {/* Discriminatory (for children categories only) */}
        {includeDiscriminatory && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Discriminatory
            </label>
            <select
              className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              value={formData[category].discriminatory ?? ''}
              onChange={(e) => handleSelectChange(category, 'discriminatory', e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">Select...</option>
              {DISCRIMINATORY_OPTIONS.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label} ({option.id})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );

  const renderTabContent = (tabId) => {
    switch (tabId) {
      case 'cautionStatement':
        return (
          <div className="bg-white rounded-md p-4">
            <h4 className="text-base font-medium text-gray-900 mb-4">Caution Statement</h4>
            <div className="max-w-md">
              <select
                className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                value={formData.cautionStatement ?? ''}
                onChange={(e) => handleSelectChange('cautionStatement', null, e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Select...</option>
                {CAUTION_STATEMENT_OPTIONS.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.label} ({option.id})
                  </option>
                ))}
              </select>
            </div>
          </div>
        );
      case 'review':
        return (
          <div className="bg-white rounded-md p-4">
            <h4 className="text-base font-medium text-gray-900 mb-4">Review and Submit</h4>
            <p className="text-sm text-gray-600 mb-4">
              Please review your selections before submitting.
            </p>

            {/* Selected Values Table */}
            <div className="mb-6 overflow-x-auto">
              <table className="min-w-full text-sm border border-gray-200 rounded">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">Category</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">SA</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">DA</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">HS</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700 border-b">D</th>
                    <th className="px-3 py-2 text-center font-medium text-gray-700 border-b"></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Caution Statement Row */}
                  <tr className="border-b">
                    <td className="px-3 py-2 font-medium text-gray-900">Caution Statement</td>
                    <td colSpan={4} className="px-3 py-2 text-gray-600">
                      {getOptionLabel(CAUTION_STATEMENT_OPTIONS, formData.cautionStatement)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isTabComplete('cautionStatement') ? (
                        <CheckCircleIcon className="h-4 w-4 text-green-500 inline" />
                      ) : (
                        <button type="button" className="text-xs text-indigo-600 hover:text-indigo-800" onClick={() => setSelectedTabIndex(0)}>Edit</button>
                      )}
                    </td>
                  </tr>
                  {/* Category Rows */}
                  {['femaleAdult', 'maleAdult', 'femaleAdolescent', 'maleAdolescent', 'femaleChild', 'maleChild'].map((cat, idx) => {
                    const hasD = cat === 'femaleChild' || cat === 'maleChild';
                    const tabName = tabs.find(t => t.id === cat)?.name;
                    return (
                      <tr key={cat} className="border-b">
                        <td className="px-3 py-2 font-medium text-gray-900">{tabName}</td>
                        <td className="px-3 py-2 text-gray-600">{getOptionLabel(SEXUAL_ATTRACTION_OPTIONS, formData[cat].sexualAttraction)}</td>
                        <td className="px-3 py-2 text-gray-600">{getOptionLabel(DATA_OPTIONS, formData[cat].data)}</td>
                        <td className="px-3 py-2 text-gray-600">{getOptionLabel(HIGH_SEXUAL_OPTIONS, formData[cat].highSexual)}</td>
                        <td className="px-3 py-2 text-gray-600">{hasD ? getOptionLabel(DISCRIMINATORY_OPTIONS, formData[cat].discriminatory) : ''}</td>
                        <td className="px-3 py-2 text-center">
                          {isTabComplete(cat) ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-500 inline" />
                          ) : (
                            <button type="button" className="text-xs text-indigo-600 hover:text-indigo-800" onClick={() => setSelectedTabIndex(idx + 1)}>Edit</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary Statement Section */}
            <div className="mt-6 border-t pt-6">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Summary Statement <span className="text-red-500">*</span>
              </label>

              {!areCategoriesComplete() ? (
                <div className="text-sm text-gray-500 italic">
                  Complete all categories above to enable the summary statement.
                </div>
              ) : templateLoading ? (
                <div className="text-sm text-gray-500">Loading template...</div>
              ) : (
                <>
                  <div className={`mb-2 text-sm ${isNewTemplate ? 'text-yellow-600' : 'text-green-600'}`}>
                    {isNewTemplate
                      ? 'No template exists for this combination. Please enter a summary statement.'
                      : 'Template loaded. You may edit if needed.'}
                  </div>
                  <textarea
                    rows={6}
                    className="w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={summaryStatement}
                    onChange={(e) => setSummaryStatement(e.target.value)}
                    placeholder="Enter the summary statement for this evaluation..."
                  />
                  {!summaryStatement.trim() && (
                    <p className="mt-1 text-sm text-red-500">Summary statement is required.</p>
                  )}
                </>
              )}
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-4 mt-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {!areCategoriesComplete() && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md text-sm mt-4">
                All categories must be completed before submission.
              </div>
            )}
          </div>
        );
      default:
        const includeDiscriminatory =
          tabId === 'femaleChild' ||
          tabId === 'maleChild';
        return renderCategorySection(tabId, includeDiscriminatory);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-4">
      <div className="flex bg-white rounded-lg shadow overflow-hidden min-h-[500px]">
        {/* Sidebar navigation */}
        <div className="w-64 bg-gray-50 border-r border-gray-200">
          <nav className="flex flex-col h-full py-4 sticky top-0">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSelectedTabIndex(index)}
                className={classNames(
                  selectedTabIndex === index
                    ? 'bg-indigo-50 border-l-4 border-indigo-500 text-indigo-700'
                    : 'border-l-4 border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  'flex items-center px-3 py-3 text-sm font-medium'
                )}
              >
                <span className="truncate">{tab.name}</span>
                {isTabComplete(tab.id) && (
                  <CheckCircleIcon className="ml-2 h-4 w-4 text-green-500" aria-hidden="true" />
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Main content area */}
        <div className="flex-1 p-8">
          {renderTabContent(tabs[selectedTabIndex].id)}

          {/* Navigation buttons */}
          <div className="mt-8 flex justify-between border-t pt-4">
            <button
              type="button"
              onClick={goToPreviousTab}
              disabled={selectedTabIndex === 0}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${selectedTabIndex === 0 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            {selectedTabIndex < tabs.length - 1 ? (
              <button
                type="button"
                onClick={goToNextTab}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Next
                <svg className="ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading || !isFormValid()}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${loading || !isFormValid() ? 'bg-indigo-300 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'}`}
              >
                {loading ? 'Submitting...' : 'Submit Results'}
              </button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
